import { toast } from 'sonner';
import { TASK_ATTACHMENT_MAX_BYTES, type TaskAttachment } from '@shared/types/tasks';
import { postTaskChat, uploadTaskAttachment } from '@/lib/tasks-api';
import { useAuthStore } from '@/stores/authStore';
import { optimisticEvent } from '@/stores/taskOptimistic';

/** MP4s play in place in the task; other videos (an iPhone's .mov, say) stay file links. */
export function isPlayableVideo(a: TaskAttachment): boolean {
  return a.content_type === 'video/mp4';
}

export function withinSizeLimit(files: File[]): File[] {
  return files.filter((file) => {
    if (file.size <= TASK_ATTACHMENT_MAX_BYTES) return true;
    toast.error(`${file.name} is over 25 MB`);
    return false;
  });
}

/** Upload one file; resolves to its attachment id, or null (with a toast) if it failed. */
export async function uploadFile(taskId: string, file: File): Promise<string | null> {
  const res = await uploadTaskAttachment(taskId, file);
  if (res.data) return res.data.attachment.id;
  toast.error(res.error ?? `Could not upload ${file.name}`);
  return null;
}

/** A preview of a file on this computer, shown in a message until the real one loads. */
function localAttachment(file: File, i: number): TaskAttachment {
  const url = URL.createObjectURL(file);
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
  return {
    id: `local-${i}-${file.name}`,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
    url,
    is_image: file.type.startsWith('image/'),
    created_at: new Date().toISOString(),
  };
}

/**
 * Show a chat message (text and/or files) at once, then upload what still needs uploading and
 * save it. Uploads already under way (from the chat box) are reused, and a Retry doesn't upload
 * a file twice.
 */
export function sendMessage(
  taskId: string,
  body: string,
  files: Array<{ file: File; upload?: Promise<string | null> }> = [],
  /** T-15: also post it on the task's source ("Also post to …"): named in a toast when that fails. */
  upstream?: { label: string },
  /** T-58: the factory message whose questions it answers (Send answers on that round's card). */
  answersFor?: string
): void {
  const id = crypto.randomUUID();
  const items = files.map((f) => ({ ...f }));
  const now = new Date().toISOString();
  optimisticEvent(
    taskId,
    {
      id,
      kind: 'chat',
      actor_user_id: useAuthStore.getState().user?.id ?? null,
      actor_label: null,
      via_api_key_name: null,
      body: body || null,
      payload: { ...(upstream ? { upstream: { posted: true } } : {}), ...(answersFor ? { answers_for: answersFor } : {}) },
      attachments: items.map((f, i) => localAttachment(f.file, i)),
      created_at: now,
      edited_at: null,
    },
    async () => {
      const ids: string[] = [];
      for (const item of items) {
        let attachmentId = item.upload ? await item.upload : null;
        if (!attachmentId) {
          item.upload = uploadFile(taskId, item.file);
          attachmentId = await item.upload;
        }
        if (attachmentId) ids.push(attachmentId);
      }
      if (items.length && ids.length < items.length) return { error: 'Some files could not be uploaded' };
      const res = await postTaskChat(taskId, body, ids, id, Boolean(upstream), answersFor);
      // The message is saved either way; only the copy upstream failed.
      if (upstream && res.data?.upstream_error) toast.error(`Couldn't post it to ${upstream.label}`, { description: res.data.upstream_error });
      return res;
    }
  );
}

/** Pastes and drops outside the chat box, on Board cards, and after New task: posted as a message. */
export function attachFilesToTask(taskId: string, files: File[]): void {
  const ok = withinSizeLimit(files);
  if (ok.length) sendMessage(taskId, '', ok.map((file) => ({ file })));
}
